import React, { useState, useEffect, useRef } from 'react';
import CarCard from './CarCard';
import { useTenant } from '../../context/ThemeContext';
import { PLACEHOLDER_IMAGES } from '../../config/placeholders';

interface CarCardWrapperProps {
  result: any;
}

const toTitleCase = (str: string): string => {
  if (str === '\u2014' || !str) return '\u2014';
  const germanPrepositions = new Set([
    'bei', 'an', 'in', 'vor', 'unter', '\u00fcber', 'im', 'am', 'zum', 'zur',
  ]);
  return str
    .toLowerCase()
    .split(/\s+/)
    .map((word, index) => {
      if (index === 0) return word.charAt(0).toUpperCase() + word.slice(1);
      if (germanPrepositions.has(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ')
    .replace(/-\s?([a-z\u00fc\u00e4\u00f6\u00df\u00e9\u00e8])/g, (_, p1) => `-${p1.toUpperCase()}`)
    .replace(/'([a-z\u00fc\u00e4\u00f6\u00df\u00e9\u00e8])/g, (_, p1) => `'${p1.toUpperCase()}`);
};

const CarCardWrapper: React.FC<CarCardWrapperProps> = ({ result }) => {
  const { tenant } = useTenant();

  const brandPlaceholder = PLACEHOLDER_IMAGES[tenant.id] ?? PLACEHOLDER_IMAGES.porsche;

  const [imageStatus, setImageStatus] = useState<'loading' | 'valid' | 'invalid'>('loading');
  const originalImageUrl = result.listing_image_url?.raw || '';
  const isMountedRef = useRef(true);

  const rejectedDomains = [
    'goo-net-exchange.com', 'barrett-jackson.com', 'overtonautomotive.com',
    'europeancollectibles.com', 'automotive-auctions.nl', 'mecum.com',
    'crossley-webb.com', '911uk.com', 'autoluce.com', 'mechatronik.de',
    'postwarclassic.com', 'theoctanecollection.com', 'mitula.net',
    'rennlist.com', 'driving.ca', 'hofarts.com', 'doubleclick.net',
    'topgeargreece.gr', 'beverlyhillscarclub.com', 'ibsrv.net', 'i.ebayimg.com',
  ];

  const badFilePatterns = [
    'logo', 'logos', 'icon', 'icons', 'favicon', 'notification', 'graph',
    'marque', 'brand', 'badge', 'symbol', 'emblema', 'fb_image', 'site_logo',
    'header-logo', 'full-logo', 'cropped-', 'ograph', 'wordmarque',
  ];

  useEffect(() => {
    isMountedRef.current = true;

    if (!originalImageUrl) { setImageStatus('invalid'); return; }

    const lowerUrl = originalImageUrl.toLowerCase();
    let urlObj: URL;
    try { urlObj = new URL(lowerUrl); } catch { setImageStatus('invalid'); return; }

    const hostname = urlObj.hostname;
    const pathname = urlObj.pathname;
    const filename = pathname.split('/').pop() || '';
    const filenameWithoutQuery = filename.split('?')[0].split('#')[0];

    const isRejectedDomain  = rejectedDomains.some(d => hostname.includes(d));
    const isBadFile         = badFilePatterns.some(p => filenameWithoutQuery.includes(p));
    const isSpecialCase     = lowerUrl.startsWith('data:') || filenameWithoutQuery.endsWith('.svg') || filenameWithoutQuery.endsWith('.gif');

    if (isRejectedDomain || isBadFile || isSpecialCase) { setImageStatus('invalid'); return; }

    const img = new Image();
    let timeoutId: NodeJS.Timeout;
    const MIN_WIDTH = 300;
    const MIN_HEIGHT = 200;

    const handleValidation = () => {
      if (!isMountedRef.current) return;
      clearTimeout(timeoutId);
      setImageStatus(img.naturalWidth < MIN_WIDTH || img.naturalHeight < MIN_HEIGHT ? 'invalid' : 'valid');
    };

    img.onload = handleValidation;
    img.onerror = () => { if (isMountedRef.current) { clearTimeout(timeoutId); setImageStatus('invalid'); } };
    img.src = originalImageUrl;
    timeoutId = setTimeout(() => { if (isMountedRef.current) setImageStatus('invalid'); }, 2000);

    return () => {
      isMountedRef.current = false;
      clearTimeout(timeoutId);
      img.onload = null;
      img.onerror = null;
    };
  }, [originalImageUrl]);

  const getImageUrl = () => imageStatus === 'valid' ? originalImageUrl : brandPlaceholder;

  const formatValue = (value: any) => {
    if (!value || value.toString().toLowerCase() === 'other') return '\u2014';
    return value;
  };

  const countryCode      = formatValue(result.listing_country_code_txt?.raw);
  const transmissionType = formatValue(result.listing_transmission_txt?.raw?.toString());
  const bodyType         = formatValue(result.listing_body_type_txt?.raw);
  const variant          = formatValue(result.listing_pab_variant_txt?.raw);
  const color            = formatValue(result.listing_grouped_exterior_color?.raw);
  const city             = formatValue(result.listing_city_txt?.raw);

  const horsePowerRaw = result.listing_ps?.raw;
  let horsePower = '\u2014';
  if (horsePowerRaw) {
    const hpValue = parseInt(horsePowerRaw, 10);
    if (!isNaN(hpValue) && hpValue > 0) horsePower = hpValue.toString();
  }

  const generation      = formatValue(result.listing_model_generation_txt?.raw);
  const variantSubGroup = formatValue(result.listing_variant_sub_group?.raw);
  const titleVariant    = variantSubGroup !== '\u2014' ? variantSubGroup : variant;
  const combinedType =
    generation === '\u2014' && titleVariant === '\u2014'
      ? 'Unknown'
      : `${generation}${titleVariant !== '\u2014' ? ` ${titleVariant}` : ''}`;

  const formattedCity        = toTitleCase(city);
  const formattedCountryCode = countryCode === '\u2014' ? '\u2014' : countryCode.toUpperCase();

  let formattedLocation = '\u2014';
  if (formattedCity !== '\u2014' && formattedCountryCode !== '\u2014') formattedLocation = `${formattedCity}, ${formattedCountryCode}`;
  else if (formattedCity !== '\u2014') formattedLocation = formattedCity;
  else if (formattedCountryCode !== '\u2014') formattedLocation = formattedCountryCode;

  const priceValue  = result.listing_price_value?.raw || 0;
  const roundedPrice = priceValue > 0 ? Math.ceil(priceValue / 1000) * 1000 : 0;

  const listingId = result.listing_id?.raw || result.id?.raw || '';

  return (
    <CarCard
      image={getImageUrl()}
      type={combinedType}
      location={formattedLocation}
      price={priceValue}
      roundedPrice={roundedPrice}
      currency={result.listing_price_currency?.raw || '\u2014'}
      year={result.listing_production_year?.raw || 0}
      mileage={
        result.listing_mileage_value?.raw
          ? typeof result.listing_mileage_value.raw === 'number'
            ? result.listing_mileage_value.raw
            : parseInt(result.listing_mileage_value.raw.replace(/\D/g, ''), 10) || 0
          : 0
      }
      engine={formatValue(result.listing_engine_txt?.raw)}
      transmission={{
        speed: formatValue(result.listing_transmission_speed_txt?.raw?.toString()),
        t_type: transmissionType,
      }}
      body_type={bodyType}
      variant={variant}
      horse_power={horsePower}
      color={color}
      listingUrl={result.listing_url?.raw || ''}
      config_hash_exact={result.config_hash_exact?.raw}
      config_hash_similar={result.config_hash_similar?.raw}
      listingId={listingId}
      build={formatValue(result.listing_build?.raw)}
      rawResult={result}
    />
  );
};

export default CarCardWrapper;
